import { useQuery } from '@tanstack/react-query'
import type { DataViewerDataSource } from '@viapeople/components'
import { Button, Select, Text } from '@viapeople/components'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { trpc } from '../../../../utils/trpc'
import type { PropertyEditorProps } from '../../types'

interface DataSourceEntryProps {
	source: DataViewerDataSource
	index: number
	organizationId: string
	onChange: (index: number, updated: DataViewerDataSource) => void
	onRemove: (index: number) => void
}

interface FormComponentNode {
	id: string
	name: string
	type: string
	label?: string
	children?: FormComponentNode[]
}

const collectInputComponents = (components: FormComponentNode[]): Array<{ value: string; label: string }> => {
	const results: Array<{ value: string; label: string }> = []

	const walk = (nodes: FormComponentNode[], path: string[]) => {
		for (const node of nodes) {
			const currentLabel = node.label || node.name
			const nextPath = [...path, currentLabel]

			if (node.type === 'input') {
				results.push({
					value: node.id,
					label: nextPath.join(' > '),
				})
			}

			if (node.children && node.children.length > 0) {
				walk(node.children, nextPath)
			}
		}
	}

	walk(components, [])
	return results
}

function DataSourceEntry({ source, index, organizationId, onChange, onRemove }: DataSourceEntryProps) {
	const { data: cyclesData, isLoading: cyclesLoading } = useQuery({
		...trpc.admin.cycles.search.queryOptions({
			organizationId,
			search: '',
			perPage: 200,
			page: 1,
		}),
		enabled: !!organizationId,
	})

	const { data: formsData, isLoading: formsLoading } = useQuery({
		...trpc.admin.forms.getFormsByCycle.queryOptions({
			cycleId: source.cycleId,
		}),
		enabled: !!source.cycleId,
	})

	const { data: formData, isLoading: formLoading } = useQuery({
		...trpc.admin.forms.getById.queryOptions({
			id: source.formId,
			organizationId,
		}),
		enabled: !!source.formId,
	})

	const cycleOptions = useMemo(() => {
		if (!cyclesData?.data) return []
		return cyclesData.data.map((c) => ({ value: c.id, label: c.name }))
	}, [cyclesData])

	const formOptions = useMemo(() => {
		if (!formsData) return []
		return formsData.map((f: { id: string; name: string }) => ({ value: f.id, label: f.name }))
	}, [formsData])

	const componentOptions = useMemo(() => {
		if (!formData?.components) return []
		return collectInputComponents(formData.components as FormComponentNode[])
	}, [formData])

	return (
		<div className='space-y-2 p-2 border border-neutral-200 rounded-md bg-neutral-50'>
			<div className='flex items-center justify-between'>
				<Text variant='xsmall' className='text-neutral-500'>
					Source {index + 1}
				</Text>
				<button
					type='button'
					onClick={() => onRemove(index)}
					className='text-red-500 hover:text-red-700 text-sm cursor-pointer'
					title='Remove source'>
					&times;
				</button>
			</div>

			<div>
				<Text variant='xsmall' className='block mb-1 text-left text-neutral-600'>
					Cycle
				</Text>
				<Select
					value={source.cycleId}
					onChange={(val) => onChange(index, { cycleId: val, formId: '', componentId: '' })}
					options={cycleOptions}
					placeholder={cyclesLoading ? 'Loading...' : 'Select Cycle'}
				/>
			</div>

			<div>
				<Text variant='xsmall' className='block mb-1 text-left text-neutral-600'>
					Form
				</Text>
				<Select
					value={source.formId}
					onChange={(val) => onChange(index, { ...source, formId: val, componentId: '' })}
					options={formOptions}
					placeholder={formsLoading ? 'Loading...' : source.cycleId ? 'Select Form' : 'Select a cycle first'}
					disabled={!source.cycleId}
				/>
			</div>

			<div>
				<Text variant='xsmall' className='block mb-1 text-left text-neutral-600'>
					Component
				</Text>
				<Select
					value={source.componentId}
					onChange={(val) => onChange(index, { ...source, componentId: val })}
					options={componentOptions}
					placeholder={formLoading ? 'Loading...' : source.formId ? 'Select Component' : 'Select a form first'}
					disabled={!source.formId}
				/>
			</div>
		</div>
	)
}

export function DataSourceConfigEditor({ name, label, value, onChange }: PropertyEditorProps) {
	const { organizationId } = useParams<{ organizationId: string }>()
	const dataSources = (Array.isArray(value) ? value : []) as DataViewerDataSource[]

	const handleEntryChange = (index: number, updated: DataViewerDataSource) => {
		const next = [...dataSources]
		next[index] = updated
		onChange(next)
	}

	const handleRemove = (index: number) => {
		const next = dataSources.filter((_, i) => i !== index)
		onChange(next)
	}

	const handleAdd = () => {
		onChange([...dataSources, { cycleId: '', formId: '', componentId: '' }])
	}

	return (
		<div key={name} className='space-y-2'>
			<Text variant='small' className='block text-left'>
				{label}
			</Text>
			<Text variant='hint' className='text-neutral-500'>
				Select one or more sources in order: cycle, form, then component.
			</Text>

			{dataSources.length === 0 && (
				<div className='text-center py-4 text-neutral-500 border-2 border-dashed border-neutral-300 rounded-md'>
					<Text variant='small'>No data sources configured</Text>
				</div>
			)}

			{dataSources.map((source, idx) => {
				const entryKey = source.componentId || source.formId || source.cycleId || `new-${idx}`
				const isComplete = !!source.cycleId && !!source.formId && !!source.componentId
				return (
					<div key={entryKey} className='space-y-1'>
						<DataSourceEntry
							source={source}
							index={idx}
							organizationId={organizationId as string}
							onChange={handleEntryChange}
							onRemove={handleRemove}
						/>
						{!isComplete && (
							<Text variant='hint' className='text-neutral-400'>
								Complete cycle, form, and component to activate this source.
							</Text>
						)}
					</div>
				)
			})}

			<Button variant='tertiary' size='small' onClick={handleAdd}>
				+ Add Data Source
			</Button>
		</div>
	)
}
